import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { queryClient } from "@via/api/query-client.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { getAxiosErrorMessage } from "@via/utils/helpers.tsx";
import {
  BaseSpaceDatasetListParams,
  BaseSpaceFileListParams,
  BaseSpaceProjectListParams,
  ErrorNotification,
} from "@via/utils/interfaces";
import { AxiosError } from "axios";
import {
  DataConnectionCredentials,
  FolderListDetails,
  type GCSConnectionCredentials,
  GetBaseSpaceResponseBody,
  GetFileListFromSSHResponseBody,
  GetFileListFromWebResponseBody,
  type S3ConnectionCredentials,
  UrlConnectionCredentials,
} from "common";

export const useAmazonS3FolderList = (): UseMutationResult<
  FolderListDetails,
  AxiosError | ErrorNotification,
  S3ConnectionCredentials
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: S3ConnectionCredentials) => {
      if (!payload.recursive) {
        prepareBucketPath(payload);
      }
      const res = await axios.post(
        "/api/data/v1/amazon-s3/bucket-objects",
        payload
      );
      return res.data;
    },
    onSuccess: (data, variables: S3ConnectionCredentials): void => {
      queryClient.setQueryData(["amazonS3FolderList", variables], data);
    },
    onError: error => {
      const errorMessage = getAxiosErrorMessage(error);
      setNotification({
        id: "aws-s3-connection-failed",
        message: "Connection Failed",
        details: errorMessage ?? "Amazon S3 Folders could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useGoogleCloudFolderList = (): UseMutationResult<
  FolderListDetails,
  AxiosError | ErrorNotification,
  GCSConnectionCredentials
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: GCSConnectionCredentials) => {
      if (!payload.recursive) {
        prepareBucketPath(payload);
      }
      const res = await axios.post(
        "/api/data/v1/google-cloud/bucket-objects",
        payload
      );
      return res.data;
    },
    onSuccess: (data, variables: GCSConnectionCredentials): void => {
      queryClient.setQueryData(["googleCloudFolderList", variables], data);
    },
    onError: error => {
      const errorMessage = getAxiosErrorMessage(error);
      setNotification({
        id: "gcs-connection-failed",
        message: "Connection Failed",
        details: errorMessage ?? "Google Cloud Folders could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

/** agent-5-5: the bookmarked data source, so an agent browse can answer whether Upload is offered. */
export type SshFolderListPayload = DataConnectionCredentials & {
  dataSourceId?: number;
};

export const useSSHFolderList = (): UseMutationResult<
  GetFileListFromSSHResponseBody,
  AxiosError | ErrorNotification,
  SshFolderListPayload
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: SshFolderListPayload) => {
      prepareBucketPath(payload);
      const sshPayload = {
        dir: getDirForSsh(payload),
        runEnvId: payload.savedCredentialId,
        recursive: payload.recursive,
        ...(payload.dataSourceId ? { dataSourceId: payload.dataSourceId } : {}),
      };

      const res = await axios.post("/api/data/v1/ssh/file-list", sshPayload);
      return res.data;
    },
    onSuccess: (data, variables: SshFolderListPayload): void => {
      queryClient.setQueryData(["sshFolderList", variables], data);
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message;
      setNotification({
        id: "ssh-connection-failed",
        message: "Connection Failed",
        details: errorMessage ?? "SSH Folders could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUrlFolderList = (): UseMutationResult<
  GetFileListFromWebResponseBody,
  AxiosError,
  UrlConnectionCredentials
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: UrlConnectionCredentials) => {
      try {
        prepareUrlPath(payload);
      } catch (error) {
        setNotification({
          id: "invalid-url",
          message: "Invalid URL",
          details: "Entered input is not in a valid URL format",
          variant: NOTIFICATION.ERROR,
        });
        return;
      }
      const urlPayload = {
        url: getUrlAddressForWeb(payload),
      };

      const res = await axios.post("/api/data/v1/web/file-list", urlPayload);
      res.data.Name = payload.bucketName;
      return res.data;
    },
    onSuccess: (data, variables: UrlConnectionCredentials): void => {
      queryClient.setQueryData(["urlFolderList", variables], data);
    },
    onError: error => {
      setNotification({
        id: "url-connection-failed",
        message: "Connection Failed",
        details: "Url Web Folders could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const getDirForSsh = (payload: DataConnectionCredentials): string => {
  let dir = payload.prefix
    ? payload.bucketName + payload.prefix
    : payload.bucketName;

  if (dir.charAt(0) !== "/") {
    dir = "/" + dir;
  }

  if (dir.length > 1 && dir.charAt(dir.length - 1) === "/") {
    dir = dir.slice(0, -1);
  }

  return dir;
};

const getUrlAddressForWeb = (payload: UrlConnectionCredentials): string => {
  return `${payload.bucketName}/${payload.prefix || ""}`;
};

export const prepareUrlPath = (payload: S3ConnectionCredentials): void => {
  if (!payload) {
    return payload;
  }
  payload.bucketName = payload.bucketName.trim();

  const url = new URL(payload.bucketName);
  const baseURL = `${url.protocol}//${url.host}`;
  let path = url.pathname.endsWith("/") ? url.pathname : url.pathname + "/";
  path = path.substring(1); // remove the first "/"
  payload.bucketName = baseURL;
  payload.prefix = path ? `${path}${payload.prefix || ""}` : payload.prefix;
};

/**
 * Modifies the S3 folder list payload to handle bucket names with paths.
 * AWS SDK does not accept bucket names with paths directly, so this function
 * extracts the path from the bucket name and adds it to the `prefix` property.
 *
 * @param payload - The S3 folder list payload containing accessKey, secretKey,
 *                  bucketName, region, and optional prefix.
 */
export const prepareBucketPath = (payload: S3ConnectionCredentials): void => {
  if (!payload) {
    return payload;
  }

  if (payload.bucketName === "/") {
    return;
  }

  payload.bucketName = payload.bucketName.trim();

  // Removes the "/" char at the end of bucket name if present
  payload.bucketName = payload.bucketName.endsWith("/")
    ? payload.bucketName.slice(0, -1)
    : payload.bucketName;

  // Removes the "s3://" or "gs://" or "https://" identifiers from a given bucket name if present.
  payload.bucketName = payload.bucketName
    .replace("s3://", "")
    .replace("gs://", "")
    .replace("https://", "");

  // Split the bucketName into an array of folder paths
  const folderPaths = payload.bucketName.split("/");

  // Set the original bucketName without the path
  payload.bucketName = folderPaths[0];

  // Join the remaining folder paths to create the specificPath
  const specificPath = folderPaths.slice(1).join("/");

  // Update the payload's prefix by combining specificPath and the original prefix
  payload.prefix = specificPath
    ? `${specificPath}/${payload.prefix || ""}`
    : payload.prefix;
};

export const useBaseSpaceProjectList = (): UseMutationResult<
  GetBaseSpaceResponseBody,
  AxiosError,
  BaseSpaceProjectListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: BaseSpaceProjectListParams) => {
      const res = await axios.post("/api/data/v1/basespace/projects", payload);
      return res.data;
    },
    onSuccess: (data, variables: { tokenId: number }): void => {
      queryClient.setQueryData(["baseSpaceProjectList", variables], data);
    },
    onError: error => {
      setNotification({
        id: "basespace-project-failed",
        message: "Connection Failed",
        details: "BaseSpace Projects could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useBaseSpaceDatasetList = (): UseMutationResult<
  GetBaseSpaceResponseBody,
  AxiosError,
  BaseSpaceDatasetListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: BaseSpaceDatasetListParams) => {
      const res = await axios.post(
        `/api/data/v1/basespace/projects/${payload.projectId}/datasets`,
        { tokenId: payload.tokenId }
      );
      return res.data;
    },
    onSuccess: (
      data,
      variables: { tokenId: number; projectId: string }
    ): void => {
      queryClient.setQueryData(["baseSpaceDatasetList", variables], data);
    },
    onError: error => {
      setNotification({
        id: "basespace-dataset-failed",
        message: "Connection Failed",
        details: "BaseSpace Datasets could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useBaseSpaceFileList = (): UseMutationResult<
  GetBaseSpaceResponseBody,
  AxiosError,
  BaseSpaceFileListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: BaseSpaceFileListParams) => {
      const res = await axios.post(
        `/api/data/v1/basespace/datasets/${payload.datasetId}/files`,
        { tokenId: payload.tokenId }
      );
      return res.data;
    },
    onSuccess: (
      data,
      variables: { tokenId: number; datasetId: string }
    ): void => {
      queryClient.setQueryData(["baseSpaceFileList", variables], data);
    },
    onError: error => {
      setNotification({
        id: "basespace-file-failed",
        message: "Connection Failed",
        details: "BaseSpace Files could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useBaseSpaceAllFilesInProject = (): UseMutationResult<
  GetBaseSpaceResponseBody,
  AxiosError,
  BaseSpaceDatasetListParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: BaseSpaceDatasetListParams) => {
      const res = await axios.post(
        `/api/data/v1/basespace/projects/${payload.projectId}/files`,
        { tokenId: payload.tokenId }
      );
      return res.data;
    },
    onSuccess: (
      data,
      variables: { tokenId: number; projectId: string }
    ): void => {
      queryClient.setQueryData(["baseSpaceAllFilesInProject", variables], data);
    },
    onError: error => {
      setNotification({
        id: "basespace-dataset-failed",
        message: "Connection Failed",
        details: "BaseSpace Files could not be loaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
